// Box API → screen shapes (src/types.ts). Everything that differs from the old backend is decided here once:
// UUID ids, Decimal strings → minor units, booking statuses, the listing description from `attributes`, a monthly
// booking (`months`) shown as the old "long" rental.
import { toMinor } from '@/lib/money';
import { addDays, nightsBetween, parseDay, toDay } from '@/lib/dates';
import type {
  Amenity, CalendarDay, Installment, ListingCard, ListingDetail, Me, PublicReview, Quote, Trip, TripStatus,
} from '@/types';
import type {
  RawBooking, RawBusy, RawCustomer, RawDictionaryNode, RawPlan, RawQuote, RawRating, RawResource, RawRound, RawStay,
  RawVerification,
} from './raw';

const minor = (value: string | number | null | undefined, currency: string) =>
  value == null || value === '' ? 0 : toMinor(String(value), currency);

export const minorOf = minor;

// Files of the box come as /api/v1/public/files/… — relative to the API server, not to this app (the Telegram app
// lives on its own domain, the native app on capacitor://). Make them absolute by the API base.
const API_ORIGIN = /^https?:\/\//.test(import.meta.env.VITE_API_BASE_URL ?? '') ? new URL(import.meta.env.VITE_API_BASE_URL).origin : '';
export const fileUrl = (url: string) => (url && url.startsWith('/') ? `${API_ORIGIN}${url}` : url);

const str = (value: unknown) => (typeof value === 'string' ? value : '');
const int = (value: unknown) => (typeof value === 'number' ? value : Number(value) || 0);
const bool = (value: unknown) => value === true;

/** A month is 30 nights in the old screens ("2 months" from nights / 30). */
export const NIGHTS_PER_MONTH = 30;

/** Whole months between two dates for a monthly booking (the box books whole months only, at least one). */
export function monthsBetween(checkIn: string, checkOut: string): number {
  return Math.max(1, Math.round(nightsBetween(checkIn, checkOut) / NIGHTS_PER_MONTH));
}

export function me(customer: RawCustomer, verification: RawVerification | null): Me {
  return {
    id: customer.id,
    first_name: customer.first_name,
    last_name: customer.last_name,
    email: customer.email ?? '',
    phone: customer.phone,
    // the phone in the customer profile is always a confirmed one (code or contact shared with the bot)
    phone_verified: !!customer.phone,
    identity_verified: verification?.status === 'verified',
    telegram_username: customer.telegram_username,
    telegram_photo_url: '',
    telegram_linked: !!customer.telegram_username,
    // the box bot writes to whoever opened the Mini App; there is no separate write permission to ask for
    telegram_can_write: true,
    language: customer.language === 'ru' ? 'ru' : 'en',
    is_host: false,
  };
}

/** Amenity dictionary tree → flat list by code (categories are the parents). */
export function amenityIndex(items: RawDictionaryNode[]): Map<string, Amenity> {
  const index = new Map<string, Amenity>();
  for (const category of items) {
    for (const item of category.children.length ? category.children : [category]) {
      index.set(item.code, { code: item.code, name: item.label, icon: item.icon, category: category.code });
    }
  }
  return index;
}

export function card(r: RawResource, favorite: boolean): ListingCard {
  const a = r.attributes;
  return {
    id: r.id,
    title: r.name,
    city: str(a.city),
    district: str(a.district),
    country: str(a.country),
    property_type: str(a.property_type) || 'other',
    space_type: str(a.space_type) || 'entire',
    max_guests: r.capacity,
    bedrooms: int(a.bedrooms),
    beds: int(a.beds),
    bathrooms: str(a.bathrooms) || String(a.bathrooms ?? ''),
    area_sqm: a.area_sqm == null ? null : int(a.area_sqm),
    allows_short_term: r.nightly_allowed,
    allows_long_term: !!r.monthly_price,
    currency: r.currency,
    nightly_price: minor(r.nightly_price, r.currency),
    monthly_price: r.monthly_price ? minor(r.monthly_price, r.currency) : null,
    // the storefront card carries the guest rating (core ≥ 3.7.6)
    rating_avg: r.rating_count ? String(r.rating) : null,
    rating_count: r.rating_count ?? 0,
    latitude: r.latitude,
    longitude: r.longitude,
    photos: r.photos.map((p) => fileUrl(p.url)),
    is_favorite: favorite,
    stay_total: null,
  };
}

/** Weekend price: weekday_prices {"4": "150", "5": "150"} (0 — Monday) — like the old weekend_nightly_price. */
function weekendPrice(r: RawResource): number | null {
  const price = r.weekday_prices['4'] ?? r.weekday_prices['5'];
  return price ? minor(price, r.currency) : null;
}

export function detail(r: RawResource, favorite: boolean, amenities: Map<string, Amenity>, rating: RawRating | null): ListingDetail {
  const a = r.attributes;
  const base = card(r, favorite);
  const codes = Array.isArray(a.amenities) ? (a.amenities as string[]) : [];
  return {
    ...base,
    rating_avg: rating?.average != null ? String(rating.average) : base.rating_avg,
    rating_count: rating?.count ?? base.rating_count,
    photos: r.photos.map((p) => ({ id: p.id, url: fileUrl(p.url), caption: p.caption })),
    summary: typeof r.attributes?.summary === 'string' ? r.attributes.summary : '',
    description: r.description,
    amenities: codes.map((code) => amenities.get(code) ?? { code, name: code, icon: '', category: '' }),
    // the host is the organisation of the platform: name and logo come with the storefront card (core ≥ 3.7.6)
    host: {
      id: r.organization_id ?? '',
      name: r.organization_name ?? '',
      // the storefront does not say whether the host is a private owner or an agency — no such line
      kind: '',
      about: '',
      is_verified: false,
      logo_url: r.organization_logo_url ? fileUrl(r.organization_logo_url) : null,
      created_at: '',
      listings_count: 0,
    },
    timezone: r.timezone,
    check_in_from: r.check_in_time,
    check_in_until: null,
    check_out_until: r.check_out_time,
    pets_allowed: bool(a.pets_allowed),
    smoking_allowed: bool(a.smoking_allowed),
    events_allowed: bool(a.events_allowed),
    children_allowed: a.children_allowed !== false,
    house_rules: str(a.house_rules),
    weekend_nightly_price: weekendPrice(r),
    cleaning_fee: minor(r.cleaning_fee, r.currency),
    guests_included: int(a.guests_included) || r.capacity,
    extra_guest_fee: minor(str(a.extra_guest_fee) || null, r.currency),
    weekly_discount_percent: int(a.weekly_discount_percent),
    monthly_discount_percent: int(a.monthly_discount_percent),
    security_deposit: minor(r.deposit, r.currency),
    utilities_included: false,
    long_term_min_months: r.min_months || 1,
    min_nights: r.min_nights,
    max_nights: r.max_nights,
    advance_notice_days: int(a.advance_notice_days),
    booking_window_days: int(a.booking_window_days) || 365,
    payment_mode: r.payment_mode === 'on_site' ? 'online_or_onsite' : 'online_only',
    cancellation_policy: r.cancellation_policy,
    require_identity_verification: r.require_verified_guest,
  };
}

/** Busy intervals [start, end) → the old per-day calendar (price — from the listing, the box has no day prices). */
export function calendar(r: RawResource, busy: RawBusy[], from: string, days: number): CalendarDay[] {
  const taken = new Set<string>();
  for (const b of busy) for (let d = b.start; d < b.end; d = addDays(d, 1)) taken.add(d);
  const result: CalendarDay[] = [];
  for (let i = 0; i < days; i++) {
    const date = addDays(from, i);
    const weekday = String((parseDay(date).getDay() + 6) % 7);
    const price = r.weekday_prices[weekday] ?? r.nightly_price;
    result.push({ date, status: taken.has(date) ? 'booked' : 'available', price: minor(price, r.currency), custom_price: weekday in r.weekday_prices });
  }
  return result;
}

const line = (q: RawQuote, code: string) => q.lines.find((l) => l.code === code)?.amount;

export function quote(q: RawQuote, checkIn: string): Quote {
  const c = q.currency;
  const monthly = q.months > 0;
  const total = minor(q.total, c);
  const online = minor(q.charge_online, c);
  const discount = -(minor(line(q, 'weekly_discount'), c) + minor(line(q, 'monthly_discount'), c));
  const installments: Quote['installments'] = [];
  if (monthly) {
    // the first month (with cleaning and deposit) is paid now, the rest — each month from the saved card
    for (let i = 0; i < q.months; i++) {
      const start = monthStart(checkIn, i);
      installments.push({
        sequence: i + 1,
        due_date: i === 0 ? toDay(new Date()) : start,
        period_start: start,
        period_end: monthStart(checkIn, i + 1),
        amount: i === 0 ? online : minor(q.month_total, c),
      });
    }
  }
  return {
    currency: c,
    rental_type: monthly ? 'long' : 'short',
    nights: monthly ? q.months * NIGHTS_PER_MONTH : q.nights,
    accommodation_amount: monthly ? minor(q.stay, c) * q.months : minor(line(q, 'nights') ?? q.stay, c),
    discount_amount: discount,
    extra_guest_amount: minor(line(q, 'extra_guests'), c),
    cleaning_fee: minor(q.cleaning_fee, c),
    subtotal_amount: total - minor(q.guest_fee, c) * (monthly ? q.months : 1) - minor(q.deposit, c),
    guest_service_fee: minor(q.guest_fee, c) * (monthly ? q.months : 1),
    security_deposit: minor(q.deposit, c),
    total_amount: total,
    payment_mode: online < total && !monthly ? 'onsite' : 'online',
    online_amount: online,
    onsite_amount: monthly ? 0 : total - online,
    installments,
  };
}

function monthStart(checkIn: string, n: number): string {
  const d = parseDay(checkIn);
  d.setMonth(d.getMonth() + n);
  return toDay(d);
}

export function reviews(rating: RawRating): PublicReview[] {
  return rating.reviews.map((r, i) => ({
    id: `${r.created}-${i}`,
    author_name: '',
    author_photo: '',
    stay_month: r.created.slice(0, 7),
    rating: r.rating,
    text: r.text,
    response: r.reply,
    published_at: r.created,
  }));
}

function status(b: RawBooking): TripStatus {
  if (b.status === 'pending') return 'pending_payment';
  if (b.status === 'cancelled') {
    if (b.cancelled_by === 'guest') return 'cancelled_by_guest';
    if (b.cancelled_by === 'host') return 'cancelled_by_host';
    return 'cancelled_by_admin';
  }
  return b.status;
}

function installments(plan: RawPlan | undefined, checkIn: string): Installment[] {
  if (!plan) return [];
  return plan.installments.map((i, n) => ({
    id: i.id,
    sequence: n + 2, // the first month is paid with the booking, the plan holds the following ones
    due_date: i.due_date,
    period_start: i.due_date,
    period_end: monthStart(checkIn, n + 2),
    amount: minor(i.amount, plan.currency),
    status: i.status === 'paid' ? 'paid' : i.status === 'failed' ? 'failed' : i.status === 'cancelled' ? 'cancelled' : 'scheduled',
    payable: i.payable,
  }));
}

/** The lines of a booking (nights / months, *_discount, extra_guests, cleaning_fee, service_fee) → the old money
 *  fields in minor units. An older booking without lines: the stay is the amount, cleaning — its own field. */
export function bookingLines(b: Pick<RawBooking, 'lines' | 'amount' | 'cleaning_fee' | 'currency'>) {
  const c = b.currency;
  const sum = (match: (code: string) => boolean) =>
    (b.lines ?? []).filter((l) => match(l.code)).reduce((acc, l) => acc + minor(l.amount, c), 0);
  const stay = sum((code) => code === 'nights' || code === 'months' || code === 'stay');
  const cleaning = sum((code) => code === 'cleaning_fee');
  return {
    stay: stay || minor(b.amount ?? '0', c),
    discount: -sum((code) => code.endsWith('_discount')),
    extra: sum((code) => code === 'extra_guests'),
    cleaning: cleaning || minor(b.cleaning_fee ?? '0', c),
    service: sum((code) => code === 'service_fee'),
  };
}

export function trip(
  b: RawBooking,
  r: RawResource | null,
  stay: RawStay | null,
  plan: RawPlan | undefined,
  round: RawRound | undefined,
): Trip {
  const c = b.currency;
  const total = minor(b.total, c);
  const deposit = minor(b.deposit, c);
  const split = bookingLines(b);
  const mine = round?.reviews.find((x) => x.direction === 'customer');
  const hold = b.status === 'pending' && r ? new Date(new Date(b.created).getTime() + r.payment_timeout_minutes * 60000).toISOString() : null;
  return {
    id: b.id,
    code: stay?.code ?? '',
    status: status(b),
    rental_type: b.months > 0 ? 'long' : 'short',
    check_in: b.check_in,
    check_out: b.check_out,
    guests: b.guests,
    payment_mode: b.payment_mode === 'on_site' ? 'onsite' : 'online',
    hold_expires_at: hold,
    currency: c,
    nights: b.nights,
    // the lines of the price kept at booking (core ≥ 3.7.6): stay, discounts, extra guests, cleaning, service fee
    accommodation_amount: split.stay,
    discount_amount: split.discount,
    extra_guest_amount: split.extra,
    cleaning_fee: split.cleaning,
    subtotal_amount: total - split.service - deposit,
    guest_service_fee: split.service,
    security_deposit: deposit,
    total_amount: total,
    online_amount: b.payment_mode === 'on_site' ? 0 : total,
    onsite_amount: b.payment_mode === 'on_site' ? total : 0,
    cancellation_policy: b.months > 0 ? r?.monthly_cancellation_policy ?? 'long_term' : r?.cancellation_policy ?? '',
    guest_name: '',
    guest_phone: '',
    message_to_host: b.note ?? '',
    listing: {
      id: b.resource_id,
      title: b.resource_name,
      city: str(r?.attributes.city),
      country: str(r?.attributes.country),
      cover: r?.photos[0]?.url ? fileUrl(r.photos[0].url) : null,
      timezone: r?.timezone ?? 'UTC',
      check_in_from: r?.check_in_time ?? '15:00:00',
      check_out_until: r?.check_out_time ?? '11:00:00',
    },
    address: b.address ? { line: b.address, apartment: '', city: str(r?.attributes.city), district: str(r?.attributes.district), postal_code: '', country: str(r?.attributes.country) } : null,
    location: b.latitude && b.longitude ? { latitude: b.latitude, longitude: b.longitude } : null,
    host: { name: '', kind: 'company', is_verified: false },
    installments: installments(plan, b.check_in),
    checkin_guide_available: !!stay?.guide,
    can_review: !!round && !round.published && !mine && new Date(round.closes_at) > new Date(),
    my_review: mine && mine.rating ? { rating: mine.rating, text: mine.text } : null,
    created_at: b.created,
    confirmed_at: null,
    cancelled_at: null,
    cancel_reason: b.cancel_reason,
  };
}

export function guide(stay: RawStay) {
  return {
    access_code: stay.access_code,
    host_note: stay.host_note,
    blocks: (stay.guide ?? []).map((g) => ({
      id: g.id,
      kind: g.kind as 'text' | 'photo' | 'video' | 'location',
      title: g.title,
      text: g.text,
      file_url: g.url ? fileUrl(g.url) : null,
      latitude: g.latitude,
      longitude: g.longitude,
      address: g.address,
    })),
  };
}
